import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Settings,
  Globe,
  Mail,
  Smartphone,
  IndianRupee,
  Save,
  RotateCcw,
  Upload,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import toast from 'react-hot-toast';

interface GeneralSettings {
  appName: string;
  tagline: string;
  supportEmail: string;
  supportPhone: string;
  minimumDeposit: number;
  maximumDeposit: number;
  minimumWithdrawal: number;
  maximumWithdrawal: number;
  referralBonus: number;
  welcomeBonus: number;
  maintenanceMode: boolean;
  maintenanceMessage: string;
}

const initialSettings: GeneralSettings = {
  appName: 'GameZone',
  tagline: 'Play & Win Real Cash',
  supportEmail: 'support@gamezone.com',
  supportPhone: '+91 1800-XXX-XXXX',
  minimumDeposit: 10,
  maximumDeposit: 50000,
  minimumWithdrawal: 100,
  maximumWithdrawal: 100000,
  referralBonus: 50,
  welcomeBonus: 100,
  maintenanceMode: false,
  maintenanceMessage: 'We are currently under maintenance. Please check back soon.',
};

export default function GeneralSettings() {
  const [settings, setSettings] = useState<GeneralSettings>(initialSettings);
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (key: keyof GeneralSettings, value: string | number | boolean) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 1500));
    toast.success('Settings saved successfully');
    setIsLoading(false);
  };

  const handleReset = () => {
    setSettings(initialSettings);
    toast.success('Settings reset to default');
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold flex items-center gap-3">
            <Settings className="w-8 h-8 text-violet-400" />
            General Settings
          </h1>
          <p className="text-slate-400 mt-1">Configure platform-wide settings</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={handleReset}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Reset
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* App Info */}
        <Card variant="glass" className="p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Globe className="w-5 h-5 text-violet-400" />
            App Information
          </h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-300">App Name</label>
              <Input
                value={settings.appName}
                onChange={(e) => handleChange('appName', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Tagline</label>
              <Input
                value={settings.tagline}
                onChange={(e) => handleChange('tagline', e.target.value)}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">App Logo</label>
              <div className="mt-2 flex items-center gap-4">
                <div className="w-20 h-20 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center text-white font-bold text-2xl">
                  GZ
                </div>
                <Button variant="outline" size="sm">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload New
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* Contact Info */}
        <Card variant="glass" className="p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Mail className="w-5 h-5 text-violet-400" />
            Contact Information
          </h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-300">Support Email</label>
              <Input
                type="email"
                value={settings.supportEmail}
                onChange={(e) => handleChange('supportEmail', e.target.value)}
                className="mt-1"
                icon={<Mail className="w-4 h-4" />}
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Support Phone</label>
              <Input
                value={settings.supportPhone}
                onChange={(e) => handleChange('supportPhone', e.target.value)}
                className="mt-1"
                icon={<Smartphone className="w-4 h-4" />}
              />
            </div>
          </div>
        </Card>

        {/* Financial Limits */}
        <Card variant="glass" className="p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-violet-400" />
            Financial Limits
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-300">Min Deposit (₹)</label>
              <Input
                type="number"
                value={settings.minimumDeposit}
                onChange={(e) => handleChange('minimumDeposit', parseInt(e.target.value))}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Max Deposit (₹)</label>
              <Input
                type="number"
                value={settings.maximumDeposit}
                onChange={(e) => handleChange('maximumDeposit', parseInt(e.target.value))}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Min Withdrawal (₹)</label>
              <Input
                type="number"
                value={settings.minimumWithdrawal}
                onChange={(e) => handleChange('minimumWithdrawal', parseInt(e.target.value))}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Max Withdrawal (₹)</label>
              <Input
                type="number"
                value={settings.maximumWithdrawal}
                onChange={(e) => handleChange('maximumWithdrawal', parseInt(e.target.value))}
                className="mt-1"
              />
            </div>
          </div>
        </Card>

        {/* Bonuses */}
        <Card variant="glass" className="p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            🎁 Bonuses
          </h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-300">Referral Bonus (₹)</label>
              <Input
                type="number"
                value={settings.referralBonus}
                onChange={(e) => handleChange('referralBonus', parseInt(e.target.value))}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Amount given when user refers a friend</p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-300">Welcome Bonus (₹)</label>
              <Input
                type="number"
                value={settings.welcomeBonus}
                onChange={(e) => handleChange('welcomeBonus', parseInt(e.target.value))}
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Signup bonus for new users</p>
            </div>
          </div>
        </Card>

        {/* Maintenance Mode */}
        <Card variant="glass" className="p-6 lg:col-span-2">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            🔧 Maintenance Mode
          </h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg">
              <div>
                <p className="font-medium">Enable Maintenance Mode</p>
                <p className="text-sm text-slate-400">Users will see maintenance message</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.maintenanceMode}
                  onChange={(e) => handleChange('maintenanceMode', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-violet-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
              </label>
            </div>
            {settings.maintenanceMode && (
              <div>
                <label className="text-sm font-medium text-slate-300">Maintenance Message</label>
                <textarea
                  value={settings.maintenanceMessage}
                  onChange={(e) => handleChange('maintenanceMessage', e.target.value)}
                  className="mt-1 w-full px-4 py-3 rounded-lg bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 resize-none"
                  rows={3}
                />
              </div>
            )}
          </div>
        </Card>
      </div>
    </motion.div>
  );
}
